import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Table, type TableColumn, type TableAction } from "@/components/Table";
import { Icon } from "@/components/Icon";
import { ModalDelete } from "@/components";
import { EditUserModal } from "@/pages/Users/EditUserModal";
import { userManagementApi } from "@/lib/api";
import { useToast } from "@/context/ToastContext";
import { useAuth } from "@/context/AuthContext";

export interface UserRole {
    id: number;
    name: string;
    tag?: string;
    tenant_id?: number;
    tenant?: { id: number; name?: string };
}

export interface UserCompany {
    id: number;
    name: string;
}

export interface UserItem {
    id: number;
    name: string;
    email: string;
    roles?: UserRole[];
    companies?: UserCompany[];
    [key: string]: unknown;
}

export interface UsersTableProps {
    items: UserItem[];
    loading?: boolean;
    error?: string | null;
    search?: string;
    onSearchChange?: (val: string) => void;
    onUpdateItem: (updated: UserItem) => void;
    onDeleteItem: (id: number | string) => void;
    onRefresh?: () => void;
    className?: string;
}

export const UsersTable: React.FC<UsersTableProps> = ({
    items,
    loading,
    error,
    search,
    onSearchChange,
    onUpdateItem,
    onDeleteItem,
    onRefresh,
    className = "",
}) => {
    const { t } = useTranslation("global");
    const { isAdmin } = useAuth();
    const { showSuccess, showError } = useToast();
    const activeCompanyId = localStorage.getItem("vigepra_active_company");
    const isMainCompany = !activeCompanyId || activeCompanyId === "main";
    const [deleteTarget, setDeleteTarget] = useState<UserItem | null>(null);
    const [editTarget, setEditTarget] = useState<UserItem | null>(null);
    const [isDeleting, setIsDeleting] = useState<boolean>(false);

    const handleConfirmDelete = async (): Promise<void> => {
        if (!deleteTarget) {
            return;
        }

        setIsDeleting(true);
        try {
            await userManagementApi.delete(deleteTarget.id);
            showSuccess(t("users.deleted"));
            const id = deleteTarget.id;
            setDeleteTarget(null);
            onDeleteItem(id);
        } catch (err: unknown) {
            const error = err as { message?: string };
            showError(error.message || t("users.error_delete"));
        } finally {
            setIsDeleting(false);
        }
    };

    const tableColumns: TableColumn<UserItem>[] = [
        {
            key: "id",
            label: "ID",
        },
        {
            key: "name",
            label: t("users.name"),
        },
        {
            key: "email",
            label: t("users.email"),
            render: (row: UserItem) => (
                <a
                    href={"mailto:" + row.email}
                    target="_blank"
                    rel="noreferrer"
                >
                    {row.email}
                </a>
            ),
        },
        ...(!isMainCompany
            ? [
                  {
                      key: "roles",
                      label: t("users.roles"),
                      sortable: false,
                      type: "multivalue" as const,
                      multiValueExtractor: (row: UserItem) => {
                          if (!row.roles || row.roles.length === 0) {
                              return [];
                          }

                          return row.roles.map((r) => {
                              const tagKey = `roles.tags.${(r.tag || r.name).toLowerCase()}`;
                              const translated = t(tagKey);
                              return translated !== tagKey
                                  ? translated
                                  : r.name;
                          });
                      },
                  },
              ]
            : []),
        ...(isMainCompany
            ? [
                  {
                      key: "roles",
                      label: t("users.roles"),
                      sortable: false,
                      type: "multivalue" as const,
                      multiValueExtractor: (row: UserItem) => {
                          if (!row.roles || row.roles.length === 0) {
                              return [];
                          }

                          return row.roles.map((role) => {
                              const tagKey = `roles.tags.${(
                                  role.tag || role.name
                              ).toLowerCase()}`;
                              const translated = t(tagKey);
                              const roleName =
                                  translated !== tagKey
                                      ? translated
                                      : role.name;
                              const companyName =
                                  role.tenant?.name ||
                                  row.companies?.find(
                                      (company) =>
                                          String(company.id) ===
                                          String(role.tenant_id)
                                  )?.name;

                              return companyName
                                  ? `${companyName}: ${roleName}`
                                  : roleName;
                          });
                      },
                  },
              ]
            : []),
        ...(isMainCompany
            ? [
                  {
                      key: "companies",
                      label: t("companies.title"),
                      sortable: false,
                      type: "multivalue" as const,
                      multiValueExtractor: (row: UserItem) => {
                          if (!row.companies || row.companies.length === 0) {
                              return [];
                          }

                          const seen = new Set<string>();
                          return row.companies
                              .map((c) => c.name)
                              .filter((name) => {
                                  if (!name || seen.has(name)) {
                                      return false;
                                  }

                                  seen.add(name);
                                  return true;
                              });
                      },
                  },
              ]
            : []),
    ];

    const tableActions: TableAction<UserItem>[] = [
        {
            label: t("table.edit"),
            icon: (
                <Icon
                    value="pencil"
                    size={14}
                    className="text-slate-600 dark:text-zinc-300"
                />
            ),
            onClick: (row: UserItem) => {
                setEditTarget(row);
            },
        },
        {
            label: t("table.delete"),
            icon: <Icon value="trash" size={14} className="text-rose-600" />,
            variant: "delete",
            onClick: (row: UserItem) => {
                setDeleteTarget(row);
            },
        },
    ];

    return (
        <div className={className}>
            <Table<UserItem>
                viewKey="vigepra_users"
                viewStorageKey="vigepra_user_views"
                appearance="workspace"
                error={error}
                onRetry={onRefresh}
                columns={tableColumns}
                data={items}
                actions={isAdmin ? tableActions : []}
                actionsPosition="right"
                searchValue={search}
                onSearchChange={onSearchChange}
                loading={loading}
                filterbox={false}
                defaultSortKey="name"
                defaultSortOrder="asc"
            />
            {deleteTarget && (
                <ModalDelete
                    isOpen={!!deleteTarget}
                    onClose={() => !isDeleting && setDeleteTarget(null)}
                    itemName={deleteTarget.email}
                    badgeText={deleteTarget.name}
                    onConfirm={handleConfirmDelete}
                    isDeleting={isDeleting}
                />
            )}

            {editTarget && (
                <EditUserModal
                    isOpen={!!editTarget}
                    onClose={() => setEditTarget(null)}
                    user={editTarget}
                    onSuccess={(updatedUser) => {
                        onUpdateItem(updatedUser as UserItem);
                    }}
                />
            )}
        </div>
    );
};
